import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { noOp } from '../../lib/utils';
import { Icon } from '../Icon';

const BASE_CHECKBOX_OUTER_CLASSES =
  'inline-block inline-flex justify-center items-center border mr-2 select-none w-6 h-6 rounded flex-shrink-0';
const DEFAULT_CHECKBOX_OUTER_CLASSES = `${BASE_CHECKBOX_OUTER_CLASSES} border-gray-500 bg-white hover:border-blue-100 focus:border-blue-100`;
const DISABLED_CHECKBOX_OUTER_CLASSES = `${BASE_CHECKBOX_OUTER_CLASSES} bg-gray-100 border-gray-200`;
const ERROR_CHECKBOX_OUTER_CLASSES = `${BASE_CHECKBOX_OUTER_CLASSES} bg-white border-error`;

export type CheckboxProps = {
  /**
   * Checked status of the checkbox
   */
  initialChecked?: boolean;
  /**
   * Label of the checkbox
   */
  label?: string;
  /**
   * Value of the checkbox
   */
  value: string | number;
  /**
   * Flag to disable checkbox
   */
  disabled?: boolean;
  /**
   * Flag for error
   */
  error?: boolean;
  /**
   * Change function for checkbox
   *
   * @return boolean if change is accepted or void
   */
  onChange?: (isChecked: boolean, value: string | number) => boolean | void;
  children?: React.ReactNode;
};

export const Checkbox: FC<CheckboxProps> = ({
  children,
  initialChecked,
  label,
  value,
  disabled,
  error,
  onChange = noOp,
}) => {
  const [isChecked, setIsChecked] = useState<boolean>(false);

  useEffect(() => setIsChecked(initialChecked || false), [initialChecked]);

  const checkboxOuterClasses = useMemo(() => {
    switch (true) {
      case disabled:
        return DISABLED_CHECKBOX_OUTER_CLASSES;
      case error:
        return ERROR_CHECKBOX_OUTER_CLASSES;
      default:
        return DEFAULT_CHECKBOX_OUTER_CLASSES;
    }
  }, [disabled, error]);

  const changeCheckbox = useCallback(() => {
    if (!disabled) {
      const newValue = !isChecked;
      // If there's a change event, let that handle things
      const changed = onChange(newValue, value);
      if (changed !== false) {
        setIsChecked(newValue);
      }
    }
  }, [disabled, setIsChecked, isChecked, value, onChange]);

  return (
    <div
      className={`flex ${
        disabled ? 'cursor-default pointer-events-none' : 'cursor-pointer'
      }`}
    >
      <button className={checkboxOuterClasses} onClick={changeCheckbox}>
        {isChecked ? (
          <div className="w-4 h-4 text-blue-100">
            <Icon name="Checkmark" />
          </div>
        ) : null}
      </button>
      <label
        className={`flex flex-grow cursor-pointer ${
          disabled ? 'text-gray-400' : 'text-gray-700'
        }`}
      >
        <input
          type="checkbox"
          className="hidden"
          checked={isChecked}
          onChange={changeCheckbox}
        />
        {children || label}
      </label>
    </div>
  );
};
